/**
 * La simulation du test — le POSTE du chatteur, avec de faux fans.
 *
 * Kayla, 06-10-2026 : « on les met en conditions réelles — c'est pas des vrais
 * gens, une simulation ». Le candidat ajoute des fans à sa file, lance le flow
 * « script 1 » sur un nouveau, le laisse tourner, annonce SA vidéo à
 * 50 €, envoie le lien de paiement, rend la main à Cyrano. Le bandeau
 * (`TaMission`) dit à chaque instant l'étape et la consigne.
 *
 * ─── LA FORME EST LE FOND (Kayla, 18-08) ──────────────────────────────────
 * « Reprendre le même design que la conversation en conditions réelles. »
 * L'écran copie donc le poste : à gauche « Ma file » / « Discussions » (les
 * lignes de `Conversation`), au centre la conversation (l'en-tête, la barre
 * Agent et « Reprendre », le fil de bulles `Conversation.Fil`, « À faire
 * maintenant », le compositeur — Emoji et Envoyer — puis sous le champ le $ Vente et
 * le flow au bout), à droite Francis · Vault · Scripts · Aide. Les composants sont ceux
 * du chatting (famille `Conversation` de `@dozka/ui`, 01-11) : le candidat qui s'y
 * retrouve ici s'y retrouvera en shift, parce que c'est le même écran.
 *
 * 🪦 Ce qui est parti le 06-10 avec la scène du 19-08 : le trombone (le poste
 * n'en a plus depuis le 10-09, la Vente est une icône sous le champ), le
 * bouton Script du composeur (les scripts vivent dans l'onglet de droite au
 * poste), la bande « Workflow ultimatum ouvert », le fil d'ouverture chauffé
 * par Cyrano.
 *
 * ⛔ AUCUN APPEL RÉSEAU ICI. Aucun vrai fan, aucune vraie commande, aucun vrai
 * flow : le moteur (`moteur.ts`) joue tout, le flow avance sur un minuteur
 * local. Le seul envoi du test est le POST du résultat, fait par la page
 * (`RegieTestCandidatePage`) une fois `onFini` appelé.
 */
import { useEffect, useMemo, useRef, useState } from "react";

import { Alerte, Avatar, Badge, Button, Carte, Conversation, Etiquette, Infobulle, Medias, Vide } from "@/shared/ui";
import { Bell, Bot, Clock, DollarSign, Play } from "@/shared/ui/icons";

import { AFaireMaintenant, BarreDuFlow } from "./BarreDuFlow";
import { Halo } from "./CoupDePouce";
import { EmojisSimples } from "./EmojisSimples";
import { ListeDesFans, type VueDeLaListe } from "./ListeDesFans";
import { PanneauDeDroite } from "./PanneauDeDroite";
import { TaMission } from "./TaMission";
import {
  TransactionModale,
  type EnvoiTransaction,
  type PreRemplissage,
} from "./TransactionModale";
import { suggestionAFaire } from "./conseils";
import {
  etatInitial,
  jouer,
  indicateurDuFlow,
  prochainDelai,
  type Action,
  type Etat,
  type Message,
  type SourceTexte,
  type ViaDeLaVente,
} from "./moteur";
import { noter, type MesureDeLIntro, type Resultat } from "./notation";
import { coupDePouce, estLaCommandeMenu, fanParId, type FanDuTest } from "./scenario";

interface Props {
  /** Appelé une fois la scène terminée, avec la note mécanique + le chrono. */
  onFini?: (resultat: Resultat) => void;
  /** Ce que l'écran d'introduction a mesuré — l'horloge du test part de SON
   *  début, pour que toutes les marques de temps soient sur le même axe. */
  intro?: MesureDeLIntro | null;
}

/** Les statuts de l'épreuve se lisent d'un coup d'œil : un cran au-dessus de la taille `sm` de l'Alerte. */
const TEXTE_DE_L_ALERTE = "text-[14.5px]";

/** La graine d'un texte inséré : ce qu'on a inséré, et d'où. */
interface Graine {
  texte: string;
  origine: "script" | "francis";
}

export function VenteScreen({ onFini, intro = null }: Props) {
  const decalage = intro?.ms ?? 0;
  const depart = useRef<number>(Date.now());
  const maintenant = () => decalage + (Date.now() - depart.current);

  const [etat, setEtat] = useState<Etat>(() => etatInitial(decalage));
  const [vue, setVue] = useState<VueDeLaListe>("file");
  const [saisie, setSaisie] = useState("");
  const [refusDuMenu, setRefusDuMenu] = useState(false);
  const [tx, setTx] = useState<{ via: ViaDeLaVente; prerempli: PreRemplissage | null } | null>(null);
  const champRef = useRef<HTMLTextAreaElement>(null);

  /**
   * Le texte inséré depuis Scripts, « À faire maintenant » ou Francis, tant
   * que le champ n'a pas été vidé — la SEULE façon honnête de distinguer « il
   * s'en est servi » de « il a écrit tout seul » (Kayla, 18-08).
   */
  const graine = useRef<Graine | null>(null);

  const avancer = (action: Action) => setEtat((prec) => jouer(prec, action, maintenant()));

  // ─── Le flow avance tout seul : un minuteur sur le pas suivant. Le moteur
  //     ne compte jamais le temps ; l'écran pose UN minuteur à la fois, qui
  //     tombe dès que l'état change (pas joué, flow arrêté).
  const delai = prochainDelai(etat);
  const indicateur = indicateurDuFlow(etat);
  const pasDuRun = etat.run?.pas ?? -1;
  useEffect(() => {
    if (delai === null) return;
    const id = setTimeout(() => avancer({ type: "flow_avance" }), delai);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delai, pasDuRun]);

  /**
   * 🩸 `onFini` se signale dans un EFFET, jamais depuis l'updater de
   * `setEtat` : React rejoue un updater en StrictMode, et le résultat
   * partirait deux fois. Le garde-fou est la ref.
   */
  const finSignalee = useRef(false);
  useEffect(() => {
    if (!etat.fini || finSignalee.current) return;
    finSignalee.current = true;
    onFini?.(noter(etat, intro));
  }, [etat, onFini, intro]);

  const ouvert = etat.ouvert;
  const fanOuvert = fanParId(ouvert);
  const fil = ouvert ? (etat.fils[ouvert] ?? []) : [];
  // Les messages consécutifs d'un même auteur se groupent (l'auteur et l'avatar ne s'écrivent
  // qu'une fois). Le fil lui-même reste collé en bas : c'est `Conversation.Fil`.
  const groupes = Conversation.grouperLesBulles(fil, { auteur: cleDAuteur, date: (m) => m.a });

  function sourceDuTexte(texte: string): SourceTexte {
    const g = graine.current;
    if (g === null) return "ecrit";
    return texte.trim() === g.texte.trim() ? g.origine : `${g.origine}_modifie`;
  }

  function inserer(texte: string, origine: "script" | "francis") {
    setSaisie((prec) => {
      const compose = prec.trim() ? `${prec}\n${texte}` : texte;
      graine.current = { texte: compose, origine };
      return compose;
    });
    setTimeout(() => champRef.current?.focus(), 0);
  }

  const envoyer = () => {
    const texte = saisie.trim();
    if (!texte || !ouvert) return;
    const source = sourceDuTexte(texte);
    setSaisie("");
    graine.current = null;
    // ⛔ `/menu` ne part pas — comme au poste depuis le 29-08-2026.
    if (estLaCommandeMenu(texte)) {
      setRefusDuMenu(true);
      return;
    }
    setRefusDuMenu(false);
    avancer({ type: "dire", fanId: ouvert, texte, source });
  };

  /** Insère un emoji AU CURSEUR. La graine ne bouge pas : insérer un emoji
   *  dans un texte venu d'un script, c'est le RETOUCHER. */
  const insererEmoji = (emoji: string) => {
    const champ = champRef.current;
    const debut = champ?.selectionStart ?? saisie.length;
    const fin = champ?.selectionEnd ?? saisie.length;
    setSaisie(saisie.slice(0, debut) + emoji + saisie.slice(fin));
    setTimeout(() => {
      champ?.focus();
      const pos = debut + emoji.length;
      champ?.setSelectionRange(pos, pos);
    }, 0);
  };

  const envoyerTransaction = (t: EnvoiTransaction) => {
    const via = tx?.via ?? "vente";
    setTx(null);
    if (!ouvert) return;
    avancer({ type: "transaction", fanId: ouvert, via, ...t });
  };

  /** « Envoyer maintenant » : on joue les pas jusqu'au prochain message du
   *  FLOW (une réponse du fan en attente passe avec lui). */
  const envoyerMaintenant = () =>
    setEtat((prec) => {
      const run = prec.run;
      if (!run) return prec;
      let e = prec;
      for (let i = 0; i < 10 && e.run && e.run.envoyes === run.envoyes && e.run.statut === "en_cours"; i += 1) {
        e = jouer(e, { type: "flow_avance" }, maintenant());
      }
      return e;
    });

  // Changer de conversation vide le champ : le brouillon d'un fan ne doit pas
  // partir chez l'autre.
  const ouvrir = (fanId: string) => {
    if (fanId !== ouvert) {
      setSaisie("");
      graine.current = null;
      setRefusDuMenu(false);
    }
    avancer({ type: "ouvrir", fanId });
  };

  const aFaire = useMemo(() => suggestionAFaire(etat, ouvert), [etat, ouvert]);

  // ─── Le rond du tutoriel : ce qu'on montre du doigt EN CE MOMENT. « À faire
  //     maintenant » n'existe qu'une fois un nouveau fan ouvert — avant, c'est
  //     « Ma file » qu'il faut viser.
  const pouce = coupDePouce(etat.etape);
  let cible = pouce?.cible ?? null;
  if (cible === "a_faire" && !aFaire) cible = "ma_file";
  if (cible === "discussions" && vue === "discussions") cible = null;
  if (cible === "ma_file" && vue === "file" && etat.file.length > 0 && !ouvert) cible = null;
  const vise = (c: string) => cible === c && !saisie.trim();

  const runIci = etat.run && etat.run.fanId === ouvert ? etat.run : null;
  const flowTourneIci = runIci?.statut === "en_cours";

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <TaMission etape={etat.etape} />

      <div className="flex min-h-0 flex-1 flex-col gap-3 min-[900px]:flex-row">
        <ListeDesFans
          etat={etat}
          vue={vue}
          onVue={setVue}
          onAjouter={(fanId) => avancer({ type: "ajouter", fanId })}
          onOuvrir={ouvrir}
          vise={cible === "discussions" || cible === "ma_file" ? cible : null}
        />

        <Carte
          remplissage="aucun"
          role="region"
          aria-label="La conversation"
          className="flex min-h-[360px] min-w-0 flex-1 flex-col max-[899px]:flex-none"
        >
          {!fanOuvert ? (
            <div className="flex flex-1 items-center justify-center">
              <Vide titre="Ouvre une conversation dans « Ma file » pour commencer." />
            </div>
          ) : (
            <>
              <Conversation.EnTeteDeConversation
                niveau="h2"
                avatar={{ graine: fanOuvert.id, nom: fanOuvert.nom, taille: 40 }}
                nom={fanOuvert.nom}
                presence={{ etat: "en_ligne", libelle: "En ligne" }}
                actions={
                  <Badge
                    size="md"
                    icon={<Bot aria-hidden />}
                    ton={etat.fini && ouvert === etat.sceneFan ? "gris" : flowTourneIci ? "vert" : "ambre"}
                    point={flowTourneIci && !etat.fini}
                  >
                    {etat.fini && ouvert === etat.sceneFan
                      ? "Cyrano a repris"
                      : flowTourneIci
                        ? "Flow en cours"
                        : "Cyrano en pause"}
                  </Badge>
                }
              />

              {/* La barre AGENT — l'agent, son état, et le bouton qui lui rend
                  la main. « Reprendre » est EXACTEMENT le geste noté `rendre`. */}
              <div className="flex items-center gap-3 px-4 py-3">
                <Avatar nom="Cyrano" taille={40} />
                <div className="flex min-w-0 flex-1 flex-col items-start gap-1.5">
                  <Etiquette>Agent</Etiquette>
                  <Badge ton="gris" point size="md">
                    Cyrano
                  </Badge>
                </div>
                {!etat.fini && (
                  <Halo actif={vise("reprendre")}>
                    <Button
                      variant="accent"
                      size="lg"
                      className="min-h-10 px-4 text-[13px]"
                      icon={<Play className="h-4 w-4" />}
                      onClick={() => avancer({ type: "rendre", fanId: fanOuvert.id })}
                    >
                      Reprendre
                    </Button>
                  </Halo>
                )}
              </div>

              {/* `key` : un autre fan, un autre fil — il repart collé en bas. Empilé (< 900 px), la
                  carte garde sa hauteur et le fil la sienne (il défile dedans) : le compositeur
                  ne passe plus sous la carte suivante. */}
              <Conversation.Fil
                key={ouvert}
                className="h-auto min-h-[200px] flex-1 max-[899px]:h-[min(60dvh,520px)] max-[899px]:min-h-[320px] max-[899px]:flex-none"
                ariaLabel="Messages"
              >
                {groupes.map(({ item, position, debutDeGroupe, finDeGroupe }) => (
                  <LigneDuFil
                    key={item.id}
                    message={item}
                    fan={fanOuvert}
                    position={position}
                    debutDeGroupe={debutDeGroupe}
                    finDeGroupe={finDeGroupe}
                  />
                ))}
                {indicateur && etat.run?.fanId === ouvert && <Conversation.Ecrit libelle={indicateur} />}
              </Conversation.Fil>

              {!etat.fini && (
                <div className="flex flex-col gap-2.5 p-3">
                  {aFaire && (
                    <Halo actif={vise("a_faire")} forme="champ" className="flex w-full">
                      <div className="w-full">
                        <AFaireMaintenant
                          key={`${ouvert}-${aFaire.genre}`}
                          suggestion={aFaire}
                          onLancer={(flowCle) =>
                            avancer({ type: "lancer_flow", fanId: fanOuvert.id, flowCle, via: "a_faire" })
                          }
                          onInserer={(t) => inserer(t, "script")}
                        />
                      </div>
                    </Halo>
                  )}

                  {/* Le composeur du poste (`Conversation.Compositeur`) : Entrée envoie, Maj+Entrée
                      va à la ligne, l'emoji se pose AU CURSEUR (`refChamp` + `insererEmoji`), le
                      sourire ouvre le Volet des emojis. Les deux statuts (flow en cours, /menu
                      refusé) sont empilés au-dessus (`bandeaux`). */}
                  <Halo actif={vise("champ")} forme="rond" className="flex w-full">
                    <div className="w-full min-w-0">
                      <Conversation.Compositeur
                        valeur={saisie}
                        onChange={(v) => {
                          if (v.trim() === "") graine.current = null;
                          setSaisie(v);
                        }}
                        onEnvoyer={envoyer}
                        placeholder="Écris un message…"
                        libelles={{ champ: "Écris un message" }}
                        refChamp={champRef}
                        hauteurMax={128}
                        bandeaux={
                          <>
                            {flowTourneIci && (
                              <Alerte
                                ton="attention"
                                taille="sm"
                                className={TEXTE_DE_L_ALERTE}
                                icone={<Clock />}
                                texte="Le flow écrit à ta place. N'écris pas tant qu'il n'est pas terminé."
                              />
                            )}
                            {refusDuMenu && (
                              <Alerte
                                ton="attention"
                                taille="sm"
                                className={TEXTE_DE_L_ALERTE}
                                titre="/menu ne s'envoie plus"
                                texte="On ne vend plus avec la carte des offres. Propose-lui UNE offre, avec son prix."
                              />
                            )}
                          </>
                        }
                        emoji={() => <EmojisSimples onChoisir={insererEmoji} />}
                      />
                    </div>
                  </Halo>

                  {/* 🗂️ LA RANGÉE DU POSTE — sous le champ (Kayla, 10-09 : « ils arrivent à mettre
                      juste des icônes et ça marche très bien »). Le $ Vente à gauche, le flow au
                      bout : les leçons disent « le bouton $ (Vente) sous le champ ». */}
                  <div className="flex min-w-0 flex-wrap items-center gap-2">
                    <Halo actif={vise("vente")}>
                      <Infobulle texte="Vente">
                        <Button
                          variant="ghost"
                          size="lg"
                          iconOnly
                          onClick={() => setTx({ via: "vente", prerempli: null })}
                          aria-label="Vente"
                          className="h-10 w-10 shrink-0"
                        >
                          <DollarSign className="h-5 w-5" />
                        </Button>
                      </Infobulle>
                    </Halo>
                    <div className="ml-auto flex min-w-0 items-center">
                      <BarreDuFlow
                        etat={etat}
                        fanId={fanOuvert.id}
                        onLancer={(flowCle) =>
                          avancer({ type: "lancer_flow", fanId: fanOuvert.id, flowCle, via: "menu" })
                        }
                        onArreter={() => avancer({ type: "arreter_flow" })}
                        onEnvoyerMaintenant={envoyerMaintenant}
                      />
                    </div>
                  </div>
                </div>
              )}

              {etat.fini && (
                <div className="p-3">
                  <Alerte
                    ton="ok"
                    taille="sm"
                    className={TEXTE_DE_L_ALERTE}
                    texte="Conversation rendue à Cyrano. C'est fini — merci."
                  />
                </div>
              )}
            </>
          )}
        </Carte>

        <PanneauDeDroite
          etat={etat}
          onInserer={inserer}
          onPreparerVente={(v) => setTx({ via: "francis", prerempli: v })}
        />
      </div>

      <TransactionModale
        ouvert={tx !== null}
        prerempli={tx?.prerempli ?? null}
        onFermer={() => setTx(null)}
        onEnvoyer={envoyerTransaction}
      />
    </div>
  );
}

/**
 * Qui parle, pour grouper : le fan, le candidat, Cyrano, le flow. Un bloc (paiement, alerte
 * « à toi ») et un message système ne se groupent avec personne.
 */
function cleDAuteur(m: Message): string | null {
  if (m.bloc === "paiement" || m.bloc === "alerte" || m.de === "systeme") return null;
  return m.de;
}

/**
 * Une ligne du fil — `Conversation.Bulle` : le fan à gauche (`entrant`), le candidat et Cyrano
 * à droite (`sortant`). TROIS voix, lisibles à l'auteur au-dessus du groupe : le fan, le
 * candidat (pastille « Toi ») et Cyrano — ce qu'il a écrit avant, et ce que le FLOW écrit
 * pendant qu'on attend (pastille FLOW ou BOT). L'avatar ne se pose qu'au bas du groupe.
 * Les blocs (paiement, alerte « à toi ») sont des `Alerte`, un message système un événement.
 */
function LigneDuFil({
  message,
  fan,
  position,
  debutDeGroupe,
  finDeGroupe,
}: {
  message: Message;
  fan: FanDuTest;
  position: Conversation.PositionDansLeGroupe;
  debutDeGroupe: boolean;
  finDeGroupe: boolean;
}) {
  // Le paiement reçu : le moteur écrit « ✅ Payé 40 € » — la coche est celle de
  // l'Alerte `ok`, le texte du moteur n'est pas touché (la coche en tête est retirée à l'affichage).
  if (message.bloc === "paiement") {
    return (
      <div className="py-1.5">
        <Alerte ton="ok" taille="sm" className={TEXTE_DE_L_ALERTE} texte={message.texte.replace(/^✅\s*/, "")} />
      </div>
    );
  }
  // L'alerte de fin de flow — le « à toi » de la prod, en évidence : c'est le
  // signal qui dit au chatteur que c'est son tour.
  if (message.bloc === "alerte") {
    return (
      <div className="py-1.5">
        <Alerte ton="attention" taille="sm" className={TEXTE_DE_L_ALERTE} icone={<Bell />} texte={message.texte} />
      </div>
    );
  }
  if (message.de === "systeme") {
    return (
      <Conversation.Bulle sens="systeme" className="py-1.5">
        {message.texte}
      </Conversation.Bulle>
    );
  }

  const deCyrano = message.de === "cyrano" || message.de === "flow";
  const deLui = message.de === "chatteur";
  const nom = deCyrano ? "Cyrano" : deLui ? "Toi" : fan.nom;
  const identite = deCyrano || deLui ? { nom } : { graine: fan.id, nom };
  const auteur = deLui ? <Badge ton="marque">{nom}</Badge> : nom;

  return (
    <Conversation.Bulle
      sens={deCyrano || deLui ? "sortant" : "entrant"}
      position={position}
      auteur={debutDeGroupe ? auteur : undefined}
      etiquettes={
        debutDeGroupe && deCyrano ? <Badge ton="gris">{message.de === "flow" ? "FLOW" : "BOT"}</Badge> : undefined
      }
      avatar={finDeGroupe ? identite : undefined}
      avecAvatar={!finDeGroupe}
      media={message.media ? <MediaDuFil media={message.media} /> : undefined}
      transcription={message.transcription ? `« ${message.transcription} »` : undefined}
    >
      {message.bloc === "transaction" && message.transaction ? (
        <>
          <span className="block font-semibold">💳 Lien de paiement envoyé</span>
          <span className="block text-[13px] text-muted">
            {(message.transaction.prixCents / 100).toFixed(0)} {message.transaction.devise}
            {message.transaction.medias.length > 0
              ? ` · délivré au paiement : ${message.transaction.medias.join(", ")}`
              : ""}
          </span>
        </>
      ) : message.texte ? (
        <Conversation.TexteRiche texte={message.texte} />
      ) : undefined}
    </Conversation.Bulle>
  );
}

/**
 * Un média envoyé par le flow — une vignette, jamais le média : le test n'a pas à montrer le
 * Vault de la créatrice (aucune adresse, la vignette porte son type et son nom). Un vocal dont
 * la prod connaît la phrase l'affiche dessous (`transcription` de la bulle) : c'est la
 * question à laquelle le fan répond.
 */
function MediaDuFil({ media }: { media: NonNullable<Message["media"]> }) {
  const libelle = { video: "🎥 Vidéo", image: "📷 Photo", vocal: "🎤 Vocal", gif: "GIF" }[media];
  return (
    <div className="w-52 max-w-full">
      <Medias.Vignette
        type={media === "video" ? "video" : media === "vocal" ? "audio" : "image"}
        titre={libelle}
        legende
        taille="sm"
        ratio={media === "vocal" ? "2 / 1" : "4 / 3"}
        paresseuse={false}
      />
    </div>
  );
}
